
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import Modal from 'components/partials/modal/modal';

import SpecialAwardPopulationFields from '../../../calculations-step/components/special-award-population-fields';

import { useCustomPopulationForm } from '../../hooks/use-custom-population-form';
import { CUSTOM_POPULATION_CRITERIA_OPTIONS } from '../../helpers/custom-population';
import type { DraftPopulation } from '../../helpers/custom-population';

import Icons from 'assets/icons';

import type { CustomPopulationRequest } from 'api/generated/schemas';

interface CustomPopulationModalProps {
  userId: string;
  modelId: string | number;
  clientId: string | number;
  population?: DraftPopulation;
  onSubmit: (definition: CustomPopulationRequest) => void;
  onRemove?: () => void;
  onClose: () => void;
}

const CustomPopulationModal = ({
  userId,
  modelId,
  clientId,
  population,
  onSubmit,
  onRemove,
  onClose,
}: CustomPopulationModalProps) => {
  const { form, data, canSubmit, submit } = useCustomPopulationForm({
    userId,
    modelId,
    clientId,
    population,
    onSubmit,
  });

  const isEditing = !!population;

  const footer = (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
        <Button
          type="button"
          variant="brand-filled"
          size="md"
          disabled={!canSubmit}
          onClick={submit}
          data-testid="submit-custom-population-button"
        >
          {isEditing ? 'Save' : 'Add'}
        </Button>
        <Button
          type="button"
          variant="brand-outlined"
          size="md"
          onClick={onClose}
          data-testid="cancel-custom-population-button"
        >
          Cancel
        </Button>
      </Stack>
      {isEditing && (
        <Button
          type="button"
          variant="reserve-text"
          size="md"
          iconBefore={<Icons.Delete aria-hidden="true" />}
          onClick={onRemove}
          data-testid="remove-custom-population-button"
        >
          Remove
        </Button>
      )}
    </Stack>
  );

  return (
    <Modal
      headerText={
        isEditing
          ? 'Edit custom admit adjustments populations'
          : 'Add custom admit adjustments populations'
      }
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      skipUnmountOnClose
      footer={footer}
      primaryAction={{ onClick: submit, disabled: !canSubmit }}
      modalClassName="!w-[640px] max-w-[92vw] !overflow-visible p-5"
      headerClassName="text-2xl font-bold"
      data-testid="custom-population-modal"
    >
      <Stack sx={{ mt: 2, gap: 2.5 }}>
        <SpecialAwardPopulationFields
          form={form}
          data={data}
          criteriaOptions={CUSTOM_POPULATION_CRITERIA_OPTIONS}
        />
        <p className="flex items-center gap-1.5 text-sm text-navy-700 font-medium">
          <Icons.Info className="size-4" aria-hidden="true" />
          Students not defined with criteria are not adjustable
        </p>
      </Stack>
    </Modal>
  );
};

export default CustomPopulationModal;
